import { useCallback, useEffect, useRef, useState } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import Rotate90DegreesCcwRoundedIcon from '@mui/icons-material/Rotate90DegreesCcwRounded';
import Rotate90DegreesCwRoundedIcon from '@mui/icons-material/Rotate90DegreesCwRounded';
import FlightRoundedIcon from '@mui/icons-material/FlightRounded';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { configureGoogleMapsLoader, googleMapsApiKey, googleMapsMapId } from '../../lib/googleMapsRuntime';
import type { LatLngPoint } from '../../lib/employerSiteViewpoints';

type Mode = 'SATELLITE' | 'HYBRID' | 'ROADMAP';

type Props = {
  building: LatLngPoint;
  employerName: string;
  /** When false the host is hidden; re-aim on the next show so the camera is not stuck at 0,0. */
  active?: boolean;
  /** 3D coverage is city-scale; the aerial tab is the guaranteed fallback. */
  onUseAerialInstead: () => void;
};

const MIN_RANGE = 80;
const MAX_RANGE = 2400;
/** Close enough to read the building massing, not the whole city. */
const BUILDING_RANGE = 220;
const BUILDING_TILT = 62;
/** Look-at height above the pin so the façade fills the frame. */
const LOOK_AT_ALTITUDE_M = 28;

async function resolveGroundMsl(lat: number, lng: number): Promise<number | null> {
  try {
    const { ElevationService } = await importLibrary('elevation');
    const svc = new ElevationService();
    const res = await svc.getElevationForLocations({ locations: [{ lat, lng }] });
    const elev = res.results[0]?.elevation;
    return typeof elev === 'number' && Number.isFinite(elev) ? elev : null;
  } catch {
    return null;
  }
}

function lookAtCamera(
  lat: number,
  lng: number,
  heading: number,
  tilt: number,
  range: number,
  groundMsl: number | null,
): google.maps.maps3d.CameraOptions {
  if (groundMsl != null) {
    return {
      center: { lat, lng, altitude: groundMsl + LOOK_AT_ALTITUDE_M },
      altitudeMode: 'ABSOLUTE',
      heading,
      tilt,
      range,
    };
  }
  return {
    center: { lat, lng, altitude: LOOK_AT_ALTITUDE_M },
    altitudeMode: 'RELATIVE_TO_GROUND',
    heading,
    tilt,
    range,
  };
}

function aimCameraAtBuilding(
  map3d: google.maps.maps3d.Map3DElement,
  lat: number,
  lng: number,
  heading: number,
  tilt: number,
  range: number,
  durationMillis: number,
  groundMsl: number | null,
) {
  const camera = lookAtCamera(lat, lng, heading, tilt, range, groundMsl);
  map3d.center = camera.center;
  map3d.heading = heading;
  map3d.tilt = tilt;
  map3d.range = range;
  const fly = map3d.flyCameraTo?.bind(map3d);
  if (!fly) return;
  try {
    fly({ endCamera: camera, durationMillis });
  } catch {
    // Constructor / property assignment already pointed the camera.
  }
}

type Phase = 'loading' | 'ready' | 'unsupported' | 'config_error';

export function EmployerSite3DStage({
  building,
  employerName,
  active = true,
  onUseAerialInstead,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.maps3d.Map3DElement | null>(null);
  const markerRef = useRef<google.maps.maps3d.Marker3DElement | null>(null);
  const groundMslRef = useRef<number | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [configError, setConfigError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('HYBRID');
  const [heading, setHeading] = useState(0);
  const [tilt, setTilt] = useState(BUILDING_TILT);
  const [range, setRange] = useState(BUILDING_RANGE);

  const { lat, lng } = building;

  useEffect(() => {
    if (!active) return;
    if (!googleMapsApiKey() || !configureGoogleMapsLoader()) {
      setPhase('config_error');
      setConfigError('Google Maps API key is not configured.');
      return;
    }

    let cancelled = false;
    if (!mapRef.current) setPhase('loading');
    setConfigError(null);

    void (async () => {
      try {
        const [{ Map3DElement, Marker3DElement }, groundMsl] = await Promise.all([
          importLibrary('maps3d'),
          resolveGroundMsl(lat, lng),
        ]);
        if (cancelled || !hostRef.current) return;

        groundMslRef.current = groundMsl;
        const lookAtAlt = (groundMsl ?? 0) + LOOK_AT_ALTITUDE_M;

        let map3d = mapRef.current;
        if (!map3d) {
          map3d = new Map3DElement({
            center: { lat, lng, altitude: lookAtAlt },
            range: BUILDING_RANGE,
            tilt: BUILDING_TILT,
            heading: 0,
            mode: 'HYBRID',
            mapId: googleMapsMapId(),
          });
          map3d.style.width = '100%';
          map3d.style.height = '100%';

          const marker = new Marker3DElement({
            position: { lat, lng, altitude: 0 },
            altitudeMode: 'CLAMP_TO_GROUND',
            label: employerName,
            extruded: true,
            drawsWhenOccluded: true,
          });
          map3d.append(marker);
          markerRef.current = marker;

          hostRef.current.replaceChildren(map3d);
          mapRef.current = map3d;
          aimCameraAtBuilding(
            map3d,
            lat,
            lng,
            0,
            BUILDING_TILT,
            BUILDING_RANGE,
            0,
            groundMsl,
          );
        } else {
          if (markerRef.current) {
            markerRef.current.position = { lat, lng, altitude: 0 };
            markerRef.current.altitudeMode = 'CLAMP_TO_GROUND';
            markerRef.current.label = employerName;
          }
          aimCameraAtBuilding(map3d, lat, lng, heading, tilt, range, 0, groundMsl);
        }

        if (!cancelled) setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err ?? '');
        const upper = message.toUpperCase();
        if (
          upper.includes('API KEY') ||
          upper.includes('REQUEST_DENIED') ||
          upper.includes('OVER_QUERY_LIMIT') ||
          upper.includes('BILLING')
        ) {
          setPhase('config_error');
          setConfigError(
            `3D Maps unavailable: ${message}. Check the API key, Maps 3D enablement, and billing.`,
          );
        } else {
          // maps3d unavailable on this key/channel or has no local coverage.
          setPhase('unsupported');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // heading/tilt/range are applied by the camera effect; do not recreate the map on orbit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lock look-at when the tab or pin changes
  }, [active, lat, lng, employerName]);

  useEffect(() => {
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
      if (hostRef.current) hostRef.current.replaceChildren();
    };
  }, []);

  useEffect(() => {
    if (!active || phase !== 'ready') return;
    const map3d = mapRef.current;
    if (!map3d) return;
    map3d.mode = mode;
    const frame = requestAnimationFrame(() => {
      aimCameraAtBuilding(
        map3d,
        lat,
        lng,
        heading,
        tilt,
        range,
        450,
        groundMslRef.current,
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [mode, heading, tilt, range, phase, active, lat, lng]);

  const orbit = useCallback((delta: number) => {
    setHeading((h) => (h + delta + 360) % 360);
  }, []);

  if (phase === 'config_error') {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack spacing={1.25} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
          <FlightRoundedIcon color="disabled" />
          <Typography variant="body2" sx={{ fontWeight: 700 }} color="error.main">
            3D view could not load
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {configError ?? 'Google Maps configuration or billing issue.'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={onUseAerialInstead}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Use 45° aerial view
          </Button>
        </Stack>
      </Box>
    );
  }

  if (phase === 'unsupported') {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack spacing={1.25} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
          <FlightRoundedIcon color="disabled" />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Photorealistic 3D is not available here
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The 3D library needs the beta Maps channel and only covers larger cities. The tilted
            satellite view gives the same building shape and roof detail.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={onUseAerialInstead}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Use 45° aerial view
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Box ref={hostRef} sx={{ height: '100%', width: '100%' }} />

      {phase === 'loading' ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : null}

      <Stack
        direction="row"
        spacing={0.75}
        sx={{ position: 'absolute', top: 10, left: 10, flexWrap: 'wrap', gap: 0.75 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next: Mode | null) => next && setMode(next)}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.25, py: 0.4 },
          }}
        >
          <ToggleButton value="SATELLITE">3D satellite</ToggleButton>
          <ToggleButton value="HYBRID">3D + labels</ToggleButton>
          <ToggleButton value="ROADMAP">3D map</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          sx={{ bgcolor: 'background.paper', boxShadow: 2, '& .MuiToggleButton-root': { px: 0.9, py: 0.4 } }}
        >
          <ToggleButton value="ccw" onClick={() => orbit(-30)} title="Orbit left">
            <Rotate90DegreesCcwRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="cw" onClick={() => orbit(30)} title="Orbit right">
            <Rotate90DegreesCwRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="in"
            onClick={() => setRange((r) => Math.max(MIN_RANGE, Math.round(r / 1.4)))}
            title="Closer"
          >
            <AddRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="out"
            onClick={() => setRange((r) => Math.min(MAX_RANGE, Math.round(r * 1.4)))}
            title="Wider"
          >
            <RemoveRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="top"
            selected={tilt < 20}
            onClick={() => setTilt((t) => (t < 20 ? BUILDING_TILT : 5))}
            title="Top-down / tilted"
          >
            <Typography variant="caption" sx={{ fontWeight: 800 }}>
              {tilt < 20 ? '3D' : 'TOP'}
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 10,
          px: 1,
          py: 0.4,
          borderRadius: 1,
          bgcolor: 'rgba(17,24,39,0.78)',
          color: '#fff',
          fontWeight: 700,
        }}
      >
        Orbit {heading}° · tilt {tilt}° · {range} m out
      </Typography>
    </Box>
  );
}
